<template>
  <div class="fund-chat" :class="{ 'fund-chat--dark': $store.getters.isDark }">
    <button v-if="!open " type="button" class="fund-chat__fab" @click="openPanel">
      <span class="fund-chat__fab-orb" aria-hidden="true">
        <v-icon small>mdi-creation</v-icon>
      </span>
      <span class="fund-chat__fab-copy">
        <span class="fund-chat__fab-label">مستشاري</span>
        <span class="fund-chat__fab-kicker">وكيل ذكي</span>
      </span>
    </button>

    <v-card v-else class="fund-chat__panel" elevation="8">
      <div class="fund-chat__head">
        <div>
          <div class="fund-chat__title">مستشاري </div>
          <div class="fund-chat__sub">وكيل ذكي</div>
        </div>
        <div>
          <v-btn icon small dark @click="restart" aria-label="محادثة جديدة">
            <v-icon small>mdi-refresh</v-icon>
          </v-btn>
          <v-btn icon small dark @click="open = false" aria-label="إغلاق">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <div ref="log" class="fund-chat__log">
        <div
          v-for="(message, index) in messages"
          :key="index"
          class="fund-chat__row"
          :class="message.role === 'user' ? 'is-user' : 'is-bot'"
        >
          <div class="fund-chat__bubble">{{ message.text }}</div>
        </div>
        <div v-if="busy" class="fund-chat__row is-bot">
          <div class="fund-chat__bubble fund-chat__bubble--wait">لحظة...</div>
        </div>
      </div>

      <div v-if="options.length" class="fund-chat__options">
        <v-chip
          v-for="option in options"
          :key="option"
          small
          outlined
          class="ma-1"
          :disabled="busy"
          @click="send(option)"
        >
          {{ option }}
        </v-chip>
      </div>

      <form class="fund-chat__form" @submit.prevent="send(draft)">
        <v-text-field
          ref="input"
          v-model="draft"
          dense
          outlined
          hide-details
          placeholder="اكتب الجواب"
          :disabled="busy"
          @keydown.enter.prevent="send(draft)"
        />
        <v-btn class="mr-2" color="#451646" dark depressed :disabled="busy || !draft.trim()" type="submit">
          إرسال
        </v-btn>
      </form>
    </v-card>
  </div>
</template>

<script>
import Axios from "axios";
import { createFundCalcSession, readDurationChange, parseAmount } from "@/components/RequestDetails/fundCalcChatEngine";

const CHAT_PATH = "/v2/employee/fund-calculator/chat";

export default {
  name: "FundCalcChat",
  props: {
    banks: { type: Array, default: () => [] },
    jobs: { type: Array, default: () => [] },
    ranks: { type: Array, default: () => [] },
    products: { type: Array, default: () => [] },
    payload: { type: Object, required: true },
    todayHijri: { type: String, default: "" },
    saved: { type: Object, default: () => ({}) },
    customerName: { type: String, default: "" },
    runFactors: { type: Function, required: true },
    runCalculate: { type: Function, required: true },
  },
  data() {
    return {
      CurrentRole: 0,
      open: false,
      busy: false,
      started: false,
      remote: false,
      endpointMissing: false,
      draft: "",
      messages: [],
      options: [],
      session: null,
      awaitingDuration: false,
      gathered: {},
    };
  },
  mounted() {
    this.CurrentRole = window.localStorage.getItem("role");
  },
  watch: {
    banks() {
      this.refreshOptions();
    },
    jobs() {
      this.refreshOptions();
    },
    ranks() {
      this.refreshOptions();
    },
    products() {
      this.refreshOptions();
    },
  },
  methods: {
    catalog() {
      return {
        banks: this.banks,
        jobs: this.jobs,
        ranks: this.ranks,
        products: this.products,
        known: this.payload,
        saved: this.saved,
        todayHijri: this.todayHijri,
      };
    },
    ensureSession() {
      if (!this.session) this.session = createFundCalcSession(this.catalog());
      else this.session.setCatalogs(this.catalog());
      return this.session;
    },
    refreshOptions() {
      const session = this.session;
      if (!session || this.remote) return;
      session.setCatalogs(this.catalog());
      const current = session.questionForCurrent && session.questionForCurrent();
      if (current && current.options && current.options.length) this.options = current.options;
    },
    async openPanel() {
      this.open = true;
      this.$nextTick(() => this.focusInput());
      if (this.started) return;
      this.started = true;
      this.busy = true;
      const remote = await this.askBackend();
      if (remote && remote.reply && !remote.error) {
        this.remote = true;
        await this.consume(remote);
      } else {
        this.endpointMissing = true;
        this.remote = false;
        await this.consume(this.ensureSession().start());
      }
      this.busy = false;
      this.scrollDown();
      this.focusInput();
    },
    restart() {
      this.messages = [];
      this.options = [];
      this.session = null;
      this.awaitingDuration = false;
      this.gathered = {};
      this.started = false;
      this.remote = false;
      this.endpointMissing = false;
      this.openPanel();
    },
    durationOverride(value) {
      const calculated = this.messages.some((message) => message.role === "assistant" && String(message.text).includes("تم الحساب"));
      const change = readDurationChange(value);
      if (!this.awaitingDuration && !change) return null;
      if (!calculated && !this.awaitingDuration) return null;
      const years = change && change.years ? change.years : (this.awaitingDuration ? parseAmount(value) : null);
      if (!years || years < 1 || years > 40) {
        this.awaitingDuration = true;
        return {
          reply: "مدة التمويل كام سنة؟",
          action: "ask",
          options: ["15", "20", "25", "30"],
          fields: {},
        };
      }
      this.awaitingDuration = false;
      this.ensureSession().setFinanceDuration(years);
      return {
        reply: `تمام، مدة التمويل ${years} سنة. بجيب النسب من الشيت وأحسب.`,
        action: "fetch_factors",
        options: [],
        fields: { finance_duration: years },
      };
    },
    push(text) {
      if (!text) return;
      this.messages.push({ role: "assistant", text });
    },
    async send(text) {
      const value = String(text || "").trim();
      if (!value || this.busy) return;
      this.messages.push({ role: "user", text: value });
      this.draft = "";
      this.options = [];
      this.busy = true;
      try {
        const durationTurn = this.durationOverride(value);
        if (durationTurn) {
          await this.consume(durationTurn);
          return;
        }
        if (this.remote) {
          const turn = await this.askBackend();
          if (!turn || turn.error || (!turn.reply && !turn.action)) {
            this.push((turn && turn.reply) || "صار خطأ في المساعد، أعد المحاولة.");
            return;
          }
          await this.consume(turn);
          return;
        }
        await this.consume(this.ensureSession().handle(value));
      } finally {
        this.busy = false;
        this.scrollDown();
        this.focusInput();
      }
    },
    rememberFields(fields) {
      Object.keys(fields || {}).forEach((key) => {
        const value = fields[key];
        if (value === undefined) return;
        if ((value === null || value === "") && key !== "military_rank") return;
        this.$set(this.gathered, key, value);
      });
    },
    formSnapshot() {
      const fromSession = this.session && this.session.formFields ? this.session.formFields() : {};
      this.rememberFields(fromSession);
      return { ...this.gathered };
    },
    async consume(turn) {
      if (!turn) return;
      this.rememberFields(turn.fields);
      if (Object.keys(this.gathered).length) this.$emit("apply", { ...this.gathered });
      if (turn.action === "fetch_factors" || turn.action === "calculate") {
        if (turn.reply) this.push(turn.reply);
        await this.$nextTick();
        const factors = await this.runFactors();
        const factorsOk = factors === true || (factors && factors.ok);
        if (!factorsOk) {
          this.push((factors && factors.summary) || "ما لقيت نسب مطابقة في الشيت. تأكد من البنك والراتب ومدة التمويل.");
          this.options = ["تعديل"];
          if (this.session) this.session.markDone();
          return;
        }
        const result = await this.runCalculate();
        const summary = (factors && factors.summary) || this.factorSummary();
        if (result && result.ok === false && result.errors && result.errors.length) {
          this.push(`${summary}\nناقص للحساب:\n${result.errors.join("\n")}`);
          return;
        }
        this.push(`${summary}\nتم الحساب، والنتيجة ظاهرة في الصفحة.`);
        this.options = ["تعديل"];
        if (this.session) this.session.markDone();
        const filled = this.formSnapshot();
        if (Object.keys(filled).length) this.$emit("apply", filled);
        return;
      }
      if (turn.reply) this.push(turn.reply);
      this.options = Array.isArray(turn.options) ? turn.options.filter(Boolean) : [];
    },
    factorSummary() {
      const data = this.payload || {};
      const lines = [
        `الاستقطاع الأول: ${this.show(data.before_retirment_deduction)}`,
        `الاستقطاع الشخصي: ${this.show(data.personal_deduction)}`,
        `هامش الربح الشخصي: ${this.show(data.profit_personal)}`,
        `الاستقطاع الثاني: ${this.show(data.after_retirment_deduction)}`,
        `هامش الربح: ${this.show(data.profit)}`,
        `مدة التمويل: ${this.show(data.finance_duration)} سنة`,
      ];
      if (Number(data.job_position) === 3) lines.push(`التمويل إلى عمر: ${this.show(data.age_limit)}`);
      return `نسب الشيت:\n${lines.join("\n")}`;
    },
    show(value) {
      return value === null || value === undefined || value === "" ? "-" : value;
    },
    apiMessages() {
      return this.messages.map((message) => ({
        role: message.role === "user" ? "user" : "assistant",
        content: message.text,
      }));
    },
    buildContext() {
      const data = this.payload || {};
      return {
        customer_name: this.customerName,
        today_hijri: this.todayHijri,
        banks: (this.banks || [])
          .filter((bank) => bank.active !== false)
          .map((bank) => ({ id: bank.id, name: bank.value || bank.name_ar || bank.text })),
        jobs: (this.jobs || []).map((job) => ({ id: job.id, name: job.value || job.name_ar || job.text })),
        ranks: (this.ranks || []).map((rank) => ({ id: rank.id, name: rank.value || rank.text || rank.name_ar })),
        products: (this.products || []).map((item) => ({
          code: item.code != null && item.code !== "" ? item.code : item.id,
          name: item.text || item.value || item.name_ar,
        })),
        known: {
          bank_id: data.bank_id,
          salary: data.salary,
          basicSalary: data.basicSalary,
          job_position: data.job_position,
          military_rank: data.military_rank,
          residential_support: data.residential_support,
          support_amount: data.support_amount,
          add_support_installment_to_salary: data.add_support_installment_to_salary,
          birthDate: data.birthDate,
          hiringDate: data.hiringDate,
          currentDate: data.currentDate || this.todayHijri,
          retirment_age: data.retirment_age,
          product_code: data.product_code,
          property_amount: data.property_amount,
          property_completed: data.property_completed,
          residence_type: data.residence_type,
          has_obligations: data.has_obligations,
          obligations_installment: data.obligations_installment,
          remaining_obligations_months: data.remaining_obligations_months,
        },
        saved: this.saved || {},
        sheet_factors: data.profit == null
          ? null
          : {
            before_retirment_deduction: data.before_retirment_deduction,
            personal_deduction: data.personal_deduction,
            profit_personal: data.profit_personal,
            after_retirment_deduction: data.after_retirment_deduction,
            profit: data.profit,
            finance_duration: data.finance_duration,
            age_limit: data.age_limit,
          },
      };
    },
    async askBackend() {
      if (this.endpointMissing || !this.$store.state.url) return null;
      try {
        const response = await Axios.post(
          `${this.$store.state.url}${CHAT_PATH}`,
          { messages: this.apiMessages(), context: this.buildContext() },
          { headers: this.request_headers() }
        );
        return this.normalizeTurn(response.data);
      } catch (error) {
        const status = error.response && error.response.status;
        if (!this.remote && (!status || status === 404 || status === 405 || status === 501)) return null;
        return { error: true, reply: "تعذر الاتصال بمساعد الحاسبة." };
      }
    },
    normalizeTurn(data) {
      if (!data || typeof data !== "object") return null;
      const body = data.payload && (data.payload.reply || data.payload.action || data.payload.fields)
        ? data.payload
        : data;
      if (body.reply || body.action || body.fields) return body;
      const content = data.choices && data.choices[0] && data.choices[0].message && data.choices[0].message.content;
      if (!content) return data.payload || null;
      try {
        return JSON.parse(content);
      } catch (error) {
        return { reply: content, action: "ask" };
      }
    },
    scrollDown() {
      this.$nextTick(() => {
        const log = this.$refs.log;
        if (log) log.scrollTop = log.scrollHeight;
      });
    },
    focusInput() {
      const input = this.$refs.input;
      if (input && input.focus) input.focus();
    },
  },
};
</script>

<style scoped>
.fund-chat {
  direction: rtl;
}
.fund-chat__fab {
  position: fixed;
  bottom: 24px;
  left: 24px;
  z-index: 30;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  padding: 7px 8px 7px 16px;
  border: 0;
  border-radius: 999px;
  color: #fff;
  cursor: pointer;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.22), transparent 46%),
    linear-gradient(115deg, #241028 0%, #5c2274 48%, #3a4fd6 100%);
  box-shadow:
    0 12px 28px rgba(45, 18, 72, 0.38),
    inset 0 0 0 1px rgba(255, 255, 255, 0.22);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.fund-chat__fab::before {
  content: "";
  position: absolute;
  inset: -6px;
  border-radius: inherit;
  background: radial-gradient(circle, rgba(124, 92, 255, 0.45), transparent 68%);
  z-index: -1;
  animation: fund-chat-pulse 2.4s ease-out infinite;
}
.fund-chat__fab:hover {
  transform: translateY(-2px);
  box-shadow:
    0 16px 34px rgba(45, 18, 72, 0.46),
    inset 0 0 0 1px rgba(255, 255, 255, 0.3);
}
.fund-chat__fab:focus-visible {
  outline: 2px solid #c9b6ff;
  outline-offset: 3px;
}
.fund-chat__fab-orb {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background:
    radial-gradient(circle at 30% 30%, #fff, transparent 36%),
    linear-gradient(145deg, #8d6bff, #d16bff 55%, #4ea2ff);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.4);
}
.fund-chat__fab-orb .v-icon {
  color: #fff !important;
}
.fund-chat__fab-copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.15;
}
.fund-chat__fab-kicker {
  font-size: 10px;
  opacity: 0.78;
}
.fund-chat__fab-label {
  font-size: 15px;
  font-weight: 700;
}
@keyframes fund-chat-pulse {
  0% { transform: scale(0.92); opacity: 0.7; }
  70% { transform: scale(1.08); opacity: 0; }
  100% { opacity: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .fund-chat__fab::before { animation: none; }
}
.fund-chat__panel {
  position: fixed;
  bottom: 24px;
  left: 24px;
  z-index: 30;
  width: min(420px, calc(100vw - 32px));
  height: min(640px, calc(100vh - 48px));
  display: flex;
  flex-direction: column;
  border-radius: 16px !important;
  overflow: hidden;
}
.fund-chat__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #451646;
  color: #fff;
  padding: 12px 8px 12px 14px;
}
.fund-chat__title {
  font-weight: 700;
  font-size: 15px;
}
.fund-chat__sub {
  font-size: 12px;
  opacity: 0.85;
}
.fund-chat__log {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  background: #faf7fb;
}
.fund-chat__row {
  display: flex;
  margin-bottom: 10px;
}
.fund-chat__row.is-user {
  justify-content: flex-start;
}
.fund-chat__row.is-bot {
  justify-content: flex-end;
}
.fund-chat__bubble {
  max-width: 85%;
  white-space: pre-wrap;
  padding: 8px 12px;
  border-radius: 12px;
  line-height: 1.6;
  font-size: 14px;
}
.is-bot .fund-chat__bubble {
  background: #fff;
  border: 1px solid #ead3eb;
  color: #3d183c;
}
.is-user .fund-chat__bubble {
  background: #451646;
  color: #fff;
}
.fund-chat__bubble--wait {
  opacity: 0.7;
}
.fund-chat__options {
  max-height: 132px;
  overflow-y: auto;
  padding: 4px 8px 0;
  background: #fff;
}
.fund-chat__form {
  display: flex;
  align-items: center;
  padding: 10px;
  background: #fff;
}
.fund-chat--dark .fund-chat__log {
  background: #2a2a2a;
}
.fund-chat--dark .is-bot .fund-chat__bubble,
.fund-chat--dark .fund-chat__options,
.fund-chat--dark .fund-chat__form {
  background: #1f1f1f;
  color: #fff;
  border-color: #5c3a5c;
}
</style>
